import React, { useCallback, useEffect, useState } from 'react';
import { Box, IconButton, InputAdornment, ListItemText, Menu, MenuItem, TextField, Tooltip } from "@mui/material";
import { Clear, Search, Settings, ViewList, Visibility, VisibilityOff } from "@mui/icons-material";
import chartingMenuSx from '../ChartingMenu.styles';
import { useTheme } from '@mui/material/styles';
import { useDispatch, useSelector } from "react-redux";
import { actions } from '../../Chart.redux';
import { useDebounce } from '@uidotdev/usehooks';



const visibilityColors = {
	dx: {
		enabled: 'rgb(20,59,255,1.0)',
		disabled: 'rgb(20,59,255,0.3)',
	},
	tx: {
		enabled: 'rgb(255,0,0,1.0)',
		disabled: 'rgb(255,0,0,0.3)',
	},
	other: {
		enabled: 'rgb(22,168,0, 1.0)',
		disabled: 'rgb(22,168,0, 0.3)',
	},
};

const SymbolSearch = ({ symbols: _symbols, setSymbols, handleClose }) => {
	const theme                                 = useTheme(),
		  sx                                    = chartingMenuSx(theme),
		  dispatch                              = useDispatch(),
		  allSymbols                            = useSelector(state => state.chart.symbols || []),
		  symbolList                            = useSelector(state => state.chart.symbolsMenu),
		  symbolShowDx                          = useSelector(state => state.chart.toolbar.symbolShowDx),
		  symbolShowTx                          = useSelector(state => state.chart.toolbar.symbolShowTx),
		  symbolShowOther                       = useSelector(state => state.chart.toolbar.symbolShowOther),
		  [ searchTerm, setSearchTerm ]         = useState(''),
		  searchTermDebounced                   = useDebounce(searchTerm, 500),
		  [ settingsAnchor, setSettingsAnchor ] = useState(null);


	const toggleSymbolVisibility = useCallback(name => e => {
		const values = { symbolShowDx, symbolShowTx, symbolShowOther };

		dispatch(actions.setToolbar({
			[ name ]: !values[ name ],
		}));
	}, [ dispatch, symbolShowDx, symbolShowTx, symbolShowOther ]);


	useEffect(() => {
		if(!allSymbols?.length) {
			setSymbols([]);
		}

		if(!symbolShowDx && !symbolShowTx && !symbolShowOther) {
			setSymbols([]);
			return;
		}

		const searchTrimmed = searchTermDebounced.trim().toLowerCase();

		if(symbolShowDx && symbolShowTx && symbolShowOther && !searchTrimmed) {
			setSymbols(symbolList);
		} else if(!searchTrimmed && (symbolShowDx || symbolShowTx || symbolShowOther)) {
			const newSymbols = symbolList.filter(symbol => {
				return (symbolShowDx && symbol.symbol_category === 'dx')
					|| (symbolShowTx && symbol.symbol_category === 'tx')
					|| (symbolShowOther && symbol.symbol_category === 'other');
			});

			setSymbols(newSymbols);
		} else {
			const newSymbols = allSymbols.filter(symbol => {
				const matchesString = !searchTrimmed
					|| `${symbol.symbol_abbreviation} ${symbol.symbol_name}`
						.toLowerCase()
						.includes(searchTrimmed);


				const matchesVisibility = (symbolShowDx && symbol.symbol_category === 'dx')
					|| (symbolShowTx && symbol.symbol_category === 'tx')
					|| (symbolShowOther && symbol.symbol_category === 'other');

				return matchesString && matchesVisibility;

			});

			setSymbols(newSymbols);
		}
	}, [ allSymbols, symbolList, symbolShowDx, symbolShowTx, symbolShowOther, searchTermDebounced ]);


	const showAllSymbols = () => {
		dispatch(actions.setToolbar({
			showAllSymbolsDialog: true,
		}));

		setSettingsAnchor(null);
		handleClose();
	};



	return (
		<Box sx={{ ...sx.menuContainer, p: 2 }}>
			<TextField
				value={searchTerm}
				onChange={(e) => setSearchTerm(e.target.value)}
				sx={{ padding: 0, marginTop: 1, marginBottom: 1 }}
				fullWidth
				placeholder="Enter search term"
				label="Search codes"
				slotProps={{ inputLabel: { shrink: true } }}
				InputProps={{
					startAdornment: (
						<InputAdornment position="start">
							<Search />
						</InputAdornment>
					),
					endAdornment: (
						<InputAdornment position="end">
							<Tooltip title="Clear search term">
								<IconButton component="span" size="large" onClick={() => setSearchTerm('')}>
									<Clear />
								</IconButton>
							</Tooltip>

							<Tooltip title="Show all codes, including AVDC codes and your custom codes.">
								<IconButton
									component="span"
									size="large"
									onClick={showAllSymbols}
								>
									<ViewList />
								</IconButton>
							</Tooltip>

							<Tooltip title="Search settings">
								<IconButton
									component="span"
									size="large"
									onClick={(e) => setSettingsAnchor(e.currentTarget)}
								>
									<Settings />
								</IconButton>
							</Tooltip>
						</InputAdornment>
					),
				}}
			/>

			<Menu
				open={Boolean(settingsAnchor)}
				anchorEl={settingsAnchor}
				onClose={() => setSettingsAnchor(null)}
			>
				<MenuItem
					onClick={toggleSymbolVisibility('symbolShowDx')}
					sx={symbolShowDx
						? sx.activeItem
						: undefined}
				>
					<Box display="flex">
						<Box sx={{ marginRight: 1 }}>
							{symbolShowDx
								? <Visibility sx={sx.dxColor} />
								: <VisibilityOff sx={{ color: visibilityColors.dx.disabled }} />}

						</Box>
						<ListItemText>{symbolShowDx
							? 'Hide'
							: 'Show'} Diagnostic Codes</ListItemText>
					</Box>
				</MenuItem>

				<MenuItem
					onClick={toggleSymbolVisibility('symbolShowTx')}
					sx={symbolShowTx
						? sx.activeItem
						: undefined}
				>
					<Box display="flex">
						<Box sx={{ marginRight: 1 }}>
							{symbolShowTx
								? <Visibility sx={sx.txColor} />
								: <VisibilityOff sx={{ color: visibilityColors.tx.disabled }} />}

						</Box>
						<ListItemText>{symbolShowTx
							? 'Hide'
							: 'Show'} Treatment Codes</ListItemText>
					</Box>
				</MenuItem>

				<MenuItem
					onClick={toggleSymbolVisibility('symbolShowOther')}
					sx={symbolShowOther
						? sx.activeItem
						: undefined}
				>
					<Box display="flex">
						<Box sx={{ marginRight: 1 }}>
							{symbolShowOther
								? <Visibility sx={sx.otherColor} />
								: <VisibilityOff sx={{ color: visibilityColors.other.disabled }} />}

						</Box>
						<ListItemText>{symbolShowOther
							? 'Hide'
							: 'Show'} Other Codes</ListItemText>
					</Box>
				</MenuItem>
			</Menu>
		</Box>
	);
};



export default SymbolSearch;
